import {
  Box,
  Checkbox,
  Grid,
  Stack,
  Typography,
  TextField,
  Divider,
  TableCell,
  TableRow,
} from '@atgfr8/atg-ui-tailwind';
import { SelectInput } from '@components';
import { BasicTable } from '@components/BasicTable';
import { Button } from '@components/Button';
import { Popover, usePopoverState } from '@components/Popover';
import { ToastType } from '@components/Toast';
import { createForm } from '@felte/solid';
import { validator } from '@felte/validator-yup';
import { PencilIcon } from '@atgfr8/icons';
import { AddProductDetails } from '@typeDefinitions/customerTypes';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { LTLCatalogs } from '@typeDefinitions/ltlTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast } from '@utils/utils';
import { freightClasses, unitTypes } from '@views/ltl';
import { HazardousMaterial } from '@views/order/components/carrier/stops/stopsEditor/HazardousMaterial';
import {
  Accessor,
  Component,
  JSX,
  Setter,
  Show,
  createEffect,
  createSignal,
} from 'solid-js';
import * as yup from 'yup';

import {
  addProductDetails,
  deleteProductDetails,
  editProductDetails,
  fetchProductById,
} from '../services';
import { ProductFormSkeleton } from './ProductFormSkeleton';

const tableCellStyles: JSX.CSSProperties = {
  'border-bottom': 'none',
  'min-width': '100px',
  'max-width': '100px',
  'word-break': 'break-all',
};

export type AddEditProductDetailsProps = {
  edit: Accessor<boolean>;
  setEdit: Setter<boolean>;
  customerId: number;
  selectedRow: Accessor<LTLCatalogs | null>;
  onModalClose: () => void;
  onAddSuccess: (row: LTLCatalogs) => void;
  onUpdateSuccess: (row: LTLCatalogs) => void;
  onDeleteSuccess: (id: string) => void;
  addChangeTrackingSource?: ChangeTrackingSource;
  updateChangeTrackingSource?: ChangeTrackingSource;
  deleteChangeTrackingSource?: ChangeTrackingSource;
};

function formatDimension(feet: number | null, inches: number | null) {
  const feetPart = feet !== null ? `${feet}'` : '';
  const inchesPart = inches !== null ? ` ${inches}"` : '';
  return `${feetPart}${inchesPart}`;
}

export const AddEditProductDetails: Component<AddEditProductDetailsProps> = (
  props,
) => {
  const [isLoading, setIsLoading] = createSignal(false);
  const [isDeleteLoading, setIsDeleteLoading] = createSignal(false);
  const hazmatPopover = usePopoverState();

  const {
    form,
    setFields,
    data,
    errors,
    isSubmitting,
    setIsSubmitting,
    reset,
  } =
    // @ts-expect-error No transform needed.
    createForm<AddProductDetails>({
      initialValues: {
        // id: '',
        unitType: 'Pallets',
        name: '',
        class: null,
        description: '',
        nmfc: '',
        hazmat: false,
        ltlCatalogItemHazmatDetail: {
          unNumber: '',
          packingGroup: '',
          hazardClass: '',
          piece: '',
          contactNumber: '',
          // lTLCatalogId: '',
          contactName: '',
          createdDate: null,
          createdBy: null,
          modifiedDate: null,
          historyContext: null,
          modifiedBy: null,
          lTLCatalog: null,
        },
        lengthFeet: null,
        lengthInches: null,
        widthFeet: null,
        widthInches: null,
        heightFeet: null,
        heightInches: null,
        note: '',
        customerId: props.customerId,
        weight: null,
      },
      extend: validator({
        schema: yup.object().shape({
          name: yup.string().required('Product Name is required.'),
          description: yup.string().required('Product Description is required'),
        }),
      }),
      onSubmit: async (values) => {
        setIsSubmitting(true);
        try {
          const id = props.selectedRow()?.id;
          if (props.edit() && Boolean(id)) {
            const res = await editProductDetails(
              { ...values, id },
              props.updateChangeTrackingSource,
            );
            if (Boolean(res)) {
              props.onUpdateSuccess({
                ...res,
                nmfc: res ? res.nmfc : '',
              } as unknown as LTLCatalogs);
              handleToast(ToastType.Success, 'Product Updated Successfully');
              props.onModalClose();
            }
          } else {
            const res = await addProductDetails(
              values,
              props.addChangeTrackingSource,
            );
            if (Boolean(res)) {
              props.onAddSuccess({
                ...res,
                nmfc: res ? res.nmfc : '',
              } as unknown as LTLCatalogs);
              handleToast(ToastType.Success, 'Product Added Successfully');
              props.onModalClose();
            }
          }
        } finally {
          setIsSubmitting(false);
        }
      },
    });

  const getProductDetailsById = async (id: string) => {
    setIsLoading(true);
    try {
      const res = await fetchProductById(id);
      setFields('name', res.name);
      setFields('description', res.description);
      setFields('unitType', res.unitType);
      setFields('class', Number(res.class));
      setFields('nmfc', res.nmfc);
      setFields('hazmat', res.hazmat);
      setFields('lengthFeet', Math.floor(res.lengthInches! / 12));
      setFields('lengthInches', Math.floor(res.lengthInches! % 12));
      setFields('widthFeet', Math.floor(res.widthInches! / 12));
      setFields('widthInches', Math.floor(res.widthInches! % 12));
      setFields('heightFeet', Math.floor(res.heightInches! / 12));
      setFields('heightInches', Math.floor(res.heightInches! % 12));
      setFields('note', res.note);
      setFields('weight', res.weight);
      setFields('ltlCatalogItemHazmatDetail', res.ltlCatalogItemHazmatDetail);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteProduct = async () => {
    if (isDeleteLoading() || isSubmitting()) return;
    setIsDeleteLoading(true);
    try {
      const id = props.selectedRow()?.id;
      if (Boolean(id)) {
        const res = await deleteProductDetails(
          id!,
          props.deleteChangeTrackingSource,
        );
        if (res) {
          props.onDeleteSuccess(id!);
          props.onModalClose();
        }
      }
    } catch (error) {
      handleToast(ToastType.Error, 'Failed to delete product');
    } finally {
      setIsDeleteLoading(false);
    }
  };

  createEffect(() => {
    const id = props.selectedRow()?.id;
    if (props.edit() && Boolean(id)) {
      void getProductDetailsById(String(id));
      return;
    }
    reset();
  });

  const columns = [
    {
      name: 'Dimensions',
      key: 'dimensions',
    },
    {
      name: 'Feet',
      key: 'feet',
    },
    {
      name: 'Inches',
      key: 'inches',
    },
  ];

  const preventingNonNumeric = (e: KeyboardEvent) => {
    if (e.key === 'e' || e.key === '-' || e.key === '+') {
      e.preventDefault();
    }
  };

  return (
    <form ref={form} class="pt-2">
      <Show when={!isLoading()} fallback={<ProductFormSkeleton />}>
        <>
          <Box style={{ 'flex-grow': 1 }}>
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, sm: 6, md: 3.5 }}>
                <Stack spacing={2}>
                  <TextField
                    label="Product Name"
                    value={data().name}
                    dataTestId={'product-name-textInput'}
                    fullWidth
                    required
                    onChange={(e: string) => setFields('name', e)}
                    error={errors().name}
                    maxLength={60}
                  />
                  <TextField
                    label="Product Description"
                    rows={3}
                    multiline
                    dataTestId={'product-description-textInput'}
                    required
                    value={data().description}
                    onChange={(e: string) => setFields('description', e)}
                    error={errors().description}
                    maxLength={150}
                  />
                  <SelectInput
                    label="Unit Type"
                    placeholder="Select Unit Type"
                    menuItems={unitTypes.map((u) => ({
                      label: u,
                      value: u,
                    }))}
                    value={data().unitType || ''}
                    onChange={(val) => setFields('unitType', val)}
                    dataTestId={formatDataTestId(
                      'Product unit type',
                      'selectInput',
                    )}
                  />
                </Stack>
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 3.5 }}>
                <Stack spacing={2}>
                  <SelectInput
                    label="Class"
                    menuItems={freightClasses.map((fc) => ({
                      label: fc,
                      value: fc,
                    }))}
                    value={data().class?.toString()}
                    onChange={(val) =>
                      setFields('class', val === '' ? null : Number(val))
                    }
                    dataTestId={formatDataTestId(
                      'Product freight class',
                      'selectInput',
                    )}
                  />
                  <TextField
                    label="NMFC"
                    dataTestId={'nmfc-textInput'}
                    fullWidth
                    value={data().nmfc}
                    onChange={(e: string) => setFields('nmfc', e)}
                    maxLength={10}
                  />
                  <TextField
                    label="Weight"
                    type="number"
                    dataTestId={'weight-textInput'}
                    fullWidth
                    value={data().weight ?? ''}
                    onChange={(e: unknown) => setFields('weight', e as number)}
                    maxLength={10}
                    onKeyPress={preventingNonNumeric}
                  />
                  <Divider />
                  <Box display="flex" gap={1} alignItems="center">
                    <Checkbox
                      dataTestId={'hazmat-checkboxInput'}
                      label="Hazmat"
                      checked={data().hazmat}
                      onChange={(e) => {
                        setFields('hazmat', e);
                        if (e === false) {
                          setFields('ltlCatalogItemHazmatDetail', null);
                        }
                      }}
                    />
                    <Popover
                      preventFormSubmit
                      forceClosePopover={!data().hazmat}
                      triggerClose={hazmatPopover.triggerClose()}
                      onClick={hazmatPopover.onTriggerClick}
                      eleName={
                        <PencilIcon
                          color={data().hazmat ? 'text-[#026EA1]' : 'gray'}
                        />
                      }
                    >
                      <HazardousMaterial
                        fromSidebar={true}
                        loadItemHazmatDetail={{
                          ...data().ltlCatalogItemHazmatDetail,
                          unNumber: data().ltlCatalogItemHazmatDetail?.unNumber,
                        }}
                        onSave={(hazmatDetail) => {
                          setFields('ltlCatalogItemHazmatDetail', {
                            unNumber: hazmatDetail.unNumber,
                            packingGroup: hazmatDetail.packingGroup,
                            hazardClass: hazmatDetail.hazardClass,
                            piece: hazmatDetail.piece!,
                            contactNumber: hazmatDetail.contactNumber,
                            contactName: hazmatDetail.contactName,
                            ltlCatalogId:
                              data().ltlCatalogItemHazmatDetail?.ltlCatalogId,
                            createdDate: null,
                            createdBy: null,
                            modifiedDate: null,
                            historyContext: null,
                            modifiedBy: null,
                            lTLCatalog: null,
                          });
                          setFields('hazmat', true);

                          hazmatPopover.close();
                        }}
                        onClose={hazmatPopover.close}
                      />
                    </Popover>
                  </Box>
                </Stack>
              </Grid>
              <Grid item size={{ xs: 12, md: 5 }}>
                <Stack spacing={2}>
                  <Divider />
                  <BasicTable columns={columns}>
                    <TableRow>
                      <TableCell style={tableCellStyles}>Length</TableCell>
                      <TableCell style={tableCellStyles}>
                        <TextField
                          type="number"
                          dataTestId={'length-feet-textInput'}
                          value={data().lengthFeet ?? ''}
                          maxLength={3}
                          onChange={(e: unknown) =>
                            setFields('lengthFeet', e as number)
                          }
                          onKeyPress={preventingNonNumeric}
                        />
                      </TableCell>
                      <TableCell style={tableCellStyles}>
                        <TextField
                          type="number"
                          maxLength={3}
                          dataTestId={'length-inches-textInput'}
                          value={data().lengthInches ?? ''}
                          onChange={(e: unknown) =>
                            setFields('lengthInches', e as number)
                          }
                          onKeyPress={preventingNonNumeric}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={tableCellStyles}>Width</TableCell>
                      <TableCell style={tableCellStyles}>
                        <TextField
                          type="number"
                          dataTestId={'width-feet-textInput'}
                          maxLength={3}
                          value={data().widthFeet ?? ''}
                          onChange={(e: unknown) =>
                            setFields('widthFeet', e as number)
                          }
                          onKeyPress={preventingNonNumeric}
                        />
                      </TableCell>
                      <TableCell style={tableCellStyles}>
                        <TextField
                          type="number"
                          maxLength={3}
                          dataTestId={'width-inches-textInput'}
                          value={data().widthInches ?? ''}
                          onChange={(e: unknown) =>
                            setFields('widthInches', e as number)
                          }
                          onKeyPress={preventingNonNumeric}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={tableCellStyles}>Height</TableCell>
                      <TableCell style={tableCellStyles}>
                        <TextField
                          type="number"
                          dataTestId={'height-feet-textInput'}
                          value={data().heightFeet ?? ''}
                          maxLength={3}
                          onChange={(e: unknown) =>
                            setFields('heightFeet', e as number)
                          }
                          onKeyPress={preventingNonNumeric}
                        />
                      </TableCell>
                      <TableCell style={tableCellStyles}>
                        <TextField
                          dataTestId={'height-inches-textInput'}
                          type="number"
                          maxLength={3}
                          value={data().heightInches ?? ''}
                          onChange={(e: unknown) =>
                            setFields('heightInches', e as number)
                          }
                          onKeyPress={preventingNonNumeric}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow
                      style={{
                        'background-color': '#DDE2EB',
                        height: '3.5rem',
                      }}
                    >
                      <TableCell style={tableCellStyles}></TableCell>
                      <TableCell colSpan={2} style={tableCellStyles}>
                        <Typography variant="body1" component="p">
                          {formatDimension(
                            data().lengthFeet,
                            data().lengthInches,
                          )}
                          {data().widthFeet !== null ||
                          data().widthInches !== null
                            ? ' x '
                            : ''}
                          {formatDimension(
                            data().widthFeet,
                            data().widthInches,
                          )}
                          {data().heightFeet !== null ||
                          data().heightInches !== null
                            ? ' x '
                            : ''}
                          {formatDimension(
                            data().heightFeet,
                            data().heightInches,
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </BasicTable>
                  <Divider />
                  <TextField
                    multiline
                    label="Note"
                    dataTestId={'note-textAreaField'}
                    rows={3}
                    value={data().note}
                    onChange={(e) => setFields('note', e)}
                    maxLength={2000}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Box>
          <Box display="flex" justifyContent="flex-end" mt="8px" gap={2}>
            <Button
              disabled={isSubmitting() || isDeleteLoading()}
              isLoading={isSubmitting()}
              variant="contained"
              color="primary"
              label={props.edit() ? 'Update Product' : 'Add Product'}
              type="submit"
              dataTestId={formatDataTestId(
                'Update Product Add Product',
                'button',
              )}
            />
            {props.edit() && (
              <Button
                disabled={isDeleteLoading() || isSubmitting()}
                isLoading={isDeleteLoading()}
                variant="contained"
                color="info"
                label="Delete Product"
                type="button"
                class="bg-[#B00020]!"
                onClick={deleteProduct}
                dataTestId={formatDataTestId('Delete Product', 'button')}
              />
            )}
          </Box>
        </>
      </Show>
    </form>
  );
};
